/**
 * The days a program meets, expanded over a date window — what a calendar needs and a list
 * never asks for.
 *
 * Related to `staff-app/programSessions.ts`, deliberately not shared with it. That module
 * answers "can attendance be recorded today", is anchored on a single day, and is a `staff-app`
 * concern; a parent-facing `utils/` module importing from `staff-app/` would invert the
 * layering the same way `programCalendar.ts` explains. The two agree on the rule that matters
 * — range, cron weekday, per-program exclusions, brand closure calendar — and this one adds
 * the two things a catalog needs: it takes a brand, and it takes `selective_dates`.
 *
 * Taking a brand is the point. `isClosedForProgramType` defaults to BHCS, so an accessor
 * called without one prints the school district's holidays onto a Kaleido calendar.
 */

import { getSessionWeekdays } from './schedule';
import { isClosedForProgramType, closureLabelForProgramType } from './programCalendar';
import { DEFAULT_BRAND_KEY, isBrandKey, type BrandKey } from '../config/brands';

export interface SessionDateProgram {
    type: string;
    schedule_cron: string;
    /** DATE column, `YYYY-MM-DD`. */
    start_date: string;
    /** DATE column, `YYYY-MM-DD`. */
    end_date: string;
    /** Vendor days off beyond the school-wide calendar. */
    excluded_dates?: string[] | null;
    /**
     * When set, the program meets on exactly these dates and the cron weekday is ignored.
     * It is `per_day`'s menu of selectable days, and it is also the only way to express a
     * series that meets on an irregular pattern — a four-Saturday workshop skipping one
     * weekend, say.
     */
    selective_dates?: string[] | null;
}

function ymdOf(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/**
 * Whether `ymd` is a meeting day, under `brand`'s closure calendar.
 *
 * Mirrors `staff-app/programSessions.isSessionDate` clause for clause, plus `selective_dates`.
 * A camp consults neither closure list — it runs on the days that close after-school — but it
 * still honours its own excluded dates, which are a different question.
 */
export function isSessionOn(
    program: SessionDateProgram,
    ymd: string,
    brand: BrandKey = DEFAULT_BRAND_KEY
): boolean {
    // DATE strings in YYYY-MM-DD compare correctly as plain strings.
    if (ymd < program.start_date || ymd > program.end_date) return false;
    if (program.excluded_dates?.includes(ymd)) return false;

    const selective = program.selective_dates;
    if (selective && selective.length > 0) return selective.includes(ymd);

    if (!getSessionWeekdays(program.schedule_cron).has(weekdayOf(ymd))) return false;
    if (program.type === 'camp') return true;
    return !isClosedForProgramType(program.type, ymd, brand);
}

/**
 * `isSessionOn` bound to a program row and its own brand — the test a per-day date picker
 * applies, so a family can only buy a day the "No class on" panel does not list.
 *
 * Takes the row's `brand_key` rather than a separate argument because every picker has the row
 * in hand, and a picker that forgot the brand would offer a Kaleido class on BHCS's calendar.
 */
export function sessionDayPredicate(
    program: SessionDateProgram & { brand_key?: string | null }
): (ymd: string) => boolean {
    const brand = isBrandKey(program.brand_key) ? program.brand_key : DEFAULT_BRAND_KEY;
    return (ymd) => isSessionOn(program, ymd, brand);
}

/** Why the program is not meeting on `ymd`, when a closure is the reason. */
export function closureReasonOn(
    program: SessionDateProgram,
    ymd: string,
    brand: BrandKey = DEFAULT_BRAND_KEY
): string | undefined {
    if (program.type === 'camp') return undefined;
    return closureLabelForProgramType(program.type, ymd, brand);
}

/** Weekday 0–6 of a `YYYY-MM-DD`, read as a local date so it never shifts a day west of UTC. */
function weekdayOf(ymd: string): number {
    const [y, m, d] = ymd.split('-').map(Number);
    return new Date(y, m - 1, d).getDay();
}

/**
 * Every meeting day in `[fromYmd, toYmd]`, ascending.
 *
 * Walks the window rather than the program's whole run, so a ten-month after-school program
 * costs one month of iterations when a month is what is on screen. Returns `[]` when the
 * window and the program do not overlap at all, which is the common case for a catalog with
 * programs spread across a year.
 */
export function sessionDatesBetween(
    program: SessionDateProgram,
    fromYmd: string,
    toYmd: string,
    brand: BrandKey = DEFAULT_BRAND_KEY
): string[] {
    const start = fromYmd > program.start_date ? fromYmd : program.start_date;
    const end = toYmd < program.end_date ? toYmd : program.end_date;
    if (start > end) return [];

    const dates: string[] = [];
    const [y, m, d] = start.split('-').map(Number);
    const cursor = new Date(y, m - 1, d);
    for (let ymd = start; ymd <= end; ) {
        if (isSessionOn(program, ymd, brand)) dates.push(ymd);
        cursor.setDate(cursor.getDate() + 1);
        ymd = ymdOf(cursor);
    }
    return dates;
}

/** The inclusive `YYYY-MM-DD` bounds of a calendar month. `month` is 0-indexed. */
export function monthBounds(year: number, month: number): { first: string; last: string } {
    return {
        first: ymdOf(new Date(year, month, 1)),
        last: ymdOf(new Date(year, month + 1, 0)),
    };
}

/**
 * The 6×7 grid of days a month view renders: the month, padded out to whole weeks with the
 * trailing days of the previous month and the leading days of the next.
 *
 * Always six rows. A five-row month next to a six-row month makes the grid jump by a row
 * height every time the parent pages forward, which reads as the page reloading.
 */
export function monthGridDates(year: number, month: number): string[] {
    const firstOfMonth = new Date(year, month, 1);
    const gridStart = new Date(year, month, 1 - firstOfMonth.getDay());
    const days: string[] = [];
    for (let i = 0; i < 42; i++) {
        days.push(ymdOf(new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i)));
    }
    return days;
}

export { ymdOf };
